import { useCallback, useEffect, useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  DeleteWithConfirmButton,
  BulkDeleteWithConfirmButton,
  useTheme,
  usePermissions,
  useGetIdentity,
} from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";
import PurchaseOrderAddDialog from "../../components/purchaseOrders/create/PurchaseOrderAddDialog";
import purchaseOrderFilterList from "../../components/purchaseOrders/filter/purchaseOrderFilterList";
import PurchaseOrderExpandList from "../../components/purchaseOrders/expand/PurchaseOrderExpandList";
import PurchaseOrderUploadDialog from "../../components/purchaseOrders/upload/PurchaseOrderUploadDialog";
import TableListActionsWithUpload from "../../components/tableListActions/TableListActionsWithUpload";
import ResetFilterButton from "../../components/tableListActions/ResetFilterButton";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const normalizeRangeFilter = (filter) => {
  const nextFilter = { ...filter };
  if (nextFilter.date) {
    nextFilter.start_date = nextFilter.start_date || nextFilter.date;
    nextFilter.end_date = nextFilter.end_date || nextFilter.date;
    delete nextFilter.date;
  }
  return nextFilter;
};

const PurchaseOrderList = () => {
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const [openAdd, setOpenAdd] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [theme] = useTheme("light");

  const currentCompanyFilter = useMemo(
    () => (identity?.companyID ? { company_id: identity.companyID } : {}),
    [identity?.companyID]
  );

  const locationFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    try {
      return normalizeRangeFilter(JSON.parse(searchParams.get("filter") || "{}"));
    } catch {
      return {};
    }
  }, [location.search]);

  useEffect(() => {
    if (!currentCompanyFilter.company_id) return;
    if (locationFilter.company_id === currentCompanyFilter.company_id) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set(
      "filter",
      JSON.stringify({
        ...locationFilter,
        company_id: currentCompanyFilter.company_id,
      })
    );

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
  }, [
    currentCompanyFilter.company_id,
    location.pathname,
    location.search,
    locationFilter,
    navigate,
  ]);

  const filterDefaultValues = useMemo(
    () => ({
      start_date: firstDate,
      end_date: lastDate,
      ...currentCompanyFilter,
    }),
    [currentCompanyFilter]
  );

  const parentFilter = useMemo(
    () => ({
      ...locationFilter,
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date: locationFilter.start_date || firstDate,
      end_date: locationFilter.end_date || lastDate,
    }),
    [currentCompanyFilter.company_id, locationFilter]
  );

  const handleClickOpenAdd = useCallback(() => {
    setOpenAdd(true);
  }, []);

  const handleClickOpenUpload = useCallback(() => {
    setOpenUpload(true);
  }, []);

  const list = useMemo(
    () => (
      <List
        title="mine.list_title.purchase_order"
        empty={false}
        actions={
          <TableListActionsWithUpload
            createHandler={handleClickOpenAdd}
            uploadHandler={handleClickOpenUpload}
            exporter={false}
            createPermission="create_purchase_order"
            extraActions={
              <ResetFilterButton defaultFilters={filterDefaultValues} />
            }
          />
        }
        filters={purchaseOrderFilterList(permissions)}
        filter={parentFilter}
        filterDefaultValues={filterDefaultValues}
        queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
      >
        <DatagridConfigurable
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          expand={<PurchaseOrderExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField label="mine.label.depo" source="company.alias" />
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <TextField label="Supplier" source="supplier.name" />
          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              label="author"
              render={(record) => {
                return (
                  <DeleteWithConfirmButton
                    confirmTitle="mine.dialog.delete_purchase_order"
                    confirmContent="mine.message.delete_purchase_order_content"
                  />
                );
              }}
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [
      filterDefaultValues,
      handleClickOpenAdd,
      handleClickOpenUpload,
      parentFilter,
      permissions,
      theme,
    ]
  );
  return (
    <>
      {list}
      <PurchaseOrderAddDialog open={openAdd} setOpen={setOpenAdd} />
      {openUpload ? (
        <PurchaseOrderUploadDialog open={openUpload} setOpen={setOpenUpload} />
      ) : null}
    </>
  );
};

export default PurchaseOrderList;
